import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Users, X } from "lucide-react";
import { joinedInLastHour } from "@/components/LiveActiveMembers";

/**
 * JoinedToast — bottom-left FOMO drip popup.
 *
 * Cycles through a pool of name+city combos every ~75s while the user is on
 * the page. Each toast shows "<Name> from <City> just joined" plus a tiny
 * "<N> joined in the last hour" stat. The hourly stat reuses the same
 * deterministic anchor math as the SocialProof live stats so numbers stay
 * consistent across the site.
 *
 * Dismissible — once the user clicks the close icon, sessionStorage flags it
 * off for that browser session (we don't want to annoy people).
 */

const SESSION_DISMISS_KEY = "fotr_join_toast_dismissed";

// First-name pool with city + neighborhood mix matching beta-city distribution
const ARRIVALS = [
  { name: "Sarah", city: "Dallas, TX" },
  { name: "Marcus", city: "Fort Worth, TX" },
  { name: "Priya", city: "Austin, TX" },
  { name: "DeShawn", city: "New Orleans, LA" },
  { name: "Mia", city: "Dallas, TX" },
  { name: "Jordan", city: "Austin, TX" },
  { name: "Tyler", city: "Fort Worth, TX" },
  { name: "Aisha", city: "New Orleans, LA" },
  { name: "Carlos", city: "Dallas, TX" },
  { name: "Leah", city: "Austin, TX" },
  { name: "Kai", city: "Fort Worth, TX" },
  { name: "Zoe", city: "New Orleans, LA" },
  { name: "Bryce", city: "Dallas, TX" },
  { name: "Naomi", city: "Austin, TX" },
  { name: "Trent", city: "New Orleans, LA" },
  { name: "Reese", city: "Fort Worth, TX" },
];

// Deterministic 2-5 per 15-min window — reuses the shared helper from
// LiveActiveMembers so numbers stay consistent across the site.
function joinedLastHour() {
  return joinedInLastHour({ min: 2, max: 5, salt: 1 });
}

function pickArrival(index) {
  return ARRIVALS[index % ARRIVALS.length];
}

export default function JoinedToast() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [arrival, setArrival] = useState(() => pickArrival(0));
  const [hourly, setHourly] = useState(() => joinedLastHour());
  const indexRef = useRef(0);
  const cycleRef = useRef(null);
  const hideRef = useRef(null);

  useEffect(() => {
    // Respect prior session dismiss
    if (typeof window !== "undefined" && sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") {
      setDismissed(true);
      return;
    }

    // Stagger arrivals — show the first one after 8s (give the hero room),
    // then re-show every 60-95s with a fresh name.
    const showOne = () => {
      const idx = indexRef.current;
      // Use a small offset based on time so visitors who reload see a new face
      const minuteSlot = Math.floor(Date.now() / 60000);
      setArrival(pickArrival(idx + minuteSlot));
      setHourly(joinedLastHour());
      setVisible(true);
      indexRef.current = idx + 1;

      // Hide after 25s — long enough for users to actually read it
      hideRef.current = setTimeout(() => setVisible(false), 25000);

      // Schedule the next one well after this one disappears (90-130s gap)
      const nextDelay = 90000 + Math.random() * 40000;
      cycleRef.current = setTimeout(showOne, nextDelay);
    };

    // First arrival lands ~4s after mount (snappier first impression)
    const firstDelay = setTimeout(showOne, 4000);

    return () => {
      clearTimeout(firstDelay);
      if (cycleRef.current) clearTimeout(cycleRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
    };
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    if (cycleRef.current) clearTimeout(cycleRef.current);
    if (hideRef.current) clearTimeout(hideRef.current);
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch {
      // sessionStorage unavailable (private mode) — fine, in-memory state still hides toast
    }
  };

  if (dismissed) return null;

  // Tiny "color" for the avatar dot — derived from the name to feel personal
  const initial = arrival.name.charAt(0).toUpperCase();
  const hash = arrival.name.charCodeAt(0) % 4;
  const avatarBg = ["bg-primary", "bg-secondary", "bg-ink", "bg-primary/80"][hash];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={`${arrival.name}-${indexRef.current}`}
          className="fixed bottom-5 left-5 z-[60] max-w-[320px] sm:max-w-[360px]"
          initial={{ opacity: 0, y: 24, x: -16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, x: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, x: -10, scale: 0.97 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          data-testid="joined-toast"
          role="status"
          aria-live="polite"
        >
          <div className="relative bg-white text-ink rounded-2xl shadow-2xl ring-1 ring-ink/10 px-4 py-3.5 pr-9 flex items-center gap-3 backdrop-blur-md">
            {/* Live pulse + avatar */}
            <div className="relative shrink-0">
              <div className={`w-10 h-10 rounded-full ${avatarBg} text-white flex items-center justify-center font-display font-black text-base shadow-sm`}>
                {initial}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 flex w-3 h-3">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex w-3 h-3 rounded-full bg-emerald-500 ring-2 ring-white" />
              </span>
            </div>

            <div className="min-w-0">
              <div className="text-sm leading-tight" data-testid="joined-toast-name">
                <span className="font-bold">{arrival.name}</span>{" "}
                <span className="text-muted">from</span>{" "}
                <span className="font-semibold">{arrival.city}</span>{" "}
                <span className="text-muted">just joined</span>
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-semibold text-primary">
                <Users size={11} />
                <span data-testid="joined-toast-hourly">{hourly}</span>
                <span className="text-muted/80 font-medium normal-case tracking-normal">
                  joined in the last hour
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDismiss}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full text-muted hover:text-ink hover:bg-line transition-colors flex items-center justify-center"
              aria-label="Dismiss notification"
              data-testid="joined-toast-dismiss"
            >
              <X size={12} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
